import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  ShieldCheck,
  Sparkles,
  ChevronRight,
  MessageCircle,
  Gauge,
  RefreshCcw,
  Wallet,
  Target,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaBand } from "@/components/sections/CtaBand";
import { ContactBlock } from "@/components/sections/ContactBlock";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";

export const Route = createFileRoute("/cases")({
  head: () => ({
    meta: [
      { title: "Cases e Resultados Reais | Kentro Sistemas" },
      {
        name: "description",
        content:
          "Veja resultados reais de empresas com a Kentro: -52% de custo de atendimento, -28% de inadimplência, +1.600 empresas e +14.670 agentes. Conheça os cases.",
      },
      { property: "og:title", content: "Cases e Resultados Reais | Kentro Sistemas" },
      {
        property: "og:description",
        content:
          "Resultados reais de empresas brasileiras com a plataforma omnichannel com IA da Kentro.",
      },
      { property: "og:url", content: `${SITE}/cases` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/cases` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "@id": `${SITE}/cases#breadcrumb`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Início",
              item: `${SITE}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Cases",
              item: `${SITE}/cases`,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${SITE}/cases#faq`,
          inLanguage: "pt-BR",
          isPartOf: { "@type": "WebPage", "@id": `${SITE}/cases` },
          mainEntity: [
            {
              "@type": "Question",
              name: "Esses resultados são garantidos?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Os números refletem resultados reais de clientes da Kentro. Cada operação varia conforme contexto, processos e maturidade da implementação.",
              },
            },
            {
              "@type": "Question",
              name: "Em quanto tempo vejo resultado?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Muitos ganhos — organização da fila, redução de retrabalho e SLA — aparecem nas primeiras semanas. Indicadores de receita e inadimplência costumam consolidar entre 60 e 90 dias.",
              },
            },
            {
              "@type": "Question",
              name: "Serve para o meu segmento?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A Kentro atende farmácias, clínicas, provedores de internet, educação, e-commerce, concessionárias, condomínios e mais — com playbooks e IA por segmento.",
              },
            },
            {
              "@type": "Question",
              name: "Como vocês comprovam os números?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Através dos dashboards e KPIs da própria operação do cliente — tempo de resolução, retomada, reabertura, conversão por canal e inadimplência.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: CasesPage,
});

// ---------- Dados agregados (reais) ----------
const aggregateStats = [
  { value: 1600, suffix: "+", label: "empresas atendidas" },
  { value: 14670, suffix: "+", label: "agentes operando todos os dias" },
  { value: 150, suffix: "%", prefix: "+", label: "de crescimento ano a ano" },
  { value: 80, suffix: "%", label: "das demandas comuns resolvidas pela IA" },
];

const segmentFilters = [
  { id: "todos", label: "Todos" },
  { id: "farmacias", label: "Farmácias", to: "/segmentos/$slug", slug: "farmacias" },
  { id: "clinicas", label: "Clínicas", to: "/segmentos/$slug", slug: "clinicas-medicas" },
  { id: "isp", label: "Provedores (ISP)", to: "/segmentos/$slug", slug: "provedores-de-internet" },
  { id: "educacao", label: "Educação", to: "/segmentos/$slug", slug: "faculdades" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "concessionarias", label: "Concessionárias", to: "/segmentos/$slug", slug: "concessionarias" },
  { id: "condominios", label: "Condomínios", to: "/segmentos/$slug", slug: "condominios" },
] as const;

type Filter = (typeof segmentFilters)[number]["id"];

type CaseCard = {
  id: string;
  filters: Filter[];
  segment: string;
  highlight: string;
  highlightLabel: string;
  challenge: string;
  what: string;
  result: string;
  link?: { to: "/segmentos/$slug"; slug: string };
  verified?: boolean;
};

const caseCards: CaseCard[] = [
  {
    id: "isp-custo",
    filters: ["isp"],
    segment: "Provedores (ISP)",
    highlight: "-52%",
    highlightLabel: "custo com atendimento",
    challenge: "Atendimento e cobrança fragmentados, com alto custo operacional.",
    what: "Omnichannel + IA + automação de chamados com SLA no Kentro ISP.",
    result: "Mesma equipe absorvendo mais chamados, com tempo de resolução menor.",
    link: { to: "/segmentos/$slug", slug: "provedores-de-internet" },
    verified: true,
  },
  {
    id: "isp-inadimplencia",
    filters: ["isp"],
    segment: "Provedores (ISP)",
    highlight: "-28%",
    highlightLabel: "inadimplência",
    challenge: "Régua de cobrança manual, boletos perdidos e baixa recuperação.",
    what: "Cobrança automática com boleto e 2ª via direto no WhatsApp.",
    result: "Recuperação de receita previsível, sem sobrecarregar o financeiro.",
    link: { to: "/segmentos/$slug", slug: "provedores-de-internet" },
    verified: true,
  },
  {
    id: "atendimento-ia",
    filters: ["todos", "farmacias", "clinicas", "isp", "educacao", "ecommerce", "concessionarias", "condominios"],
    segment: "Atendimento geral",
    highlight: "até 80%",
    highlightLabel: "das dúvidas comuns resolvidas pela IA",
    challenge: "Equipe travada respondendo as mesmas perguntas o dia inteiro.",
    what: "Agentes de IA por segmento triando, respondendo e escalando só o que precisa.",
    result: "Time humano focado em casos complexos e venda — sem aumentar headcount.",
  },
  {
    id: "farmacias-recompra",
    filters: ["farmacias"],
    segment: "Farmácias",
    highlight: "+32%",
    highlightLabel: "recompra em 30 dias",
    challenge: "Pedidos perdidos no WhatsApp e clientes sem retorno.",
    what: "CRM com histórico, lembretes de receita contínua e IA 24h.",
    result: "Cliente volta, fila some e a equipe não perde pedido por demora.",
    link: { to: "/segmentos/$slug", slug: "farmacias" },
  },
  {
    id: "clinicas-noshow",
    filters: ["clinicas"],
    segment: "Clínicas",
    highlight: "-43%",
    highlightLabel: "no-show de consultas",
    challenge: "Faltas de pacientes e agenda desorganizada entre canais.",
    what: "Confirmação, lembretes e pré-triagem automatizados pelo WhatsApp.",
    result: "Agenda mais cheia, secretária livre para o que é estratégico.",
    link: { to: "/segmentos/$slug", slug: "clinicas-medicas" },
  },
  {
    id: "educacao-matricula",
    filters: ["educacao"],
    segment: "Educação",
    highlight: "3x",
    highlightLabel: "matrículas qualificadas",
    challenge: "Captação dispersa, leads esfriando e dúvidas repetidas.",
    what: "Qualificação por IA, distribuição automática e CRM por etapa.",
    result: "Comercial sabe exatamente em quem ligar — e quando.",
    link: { to: "/segmentos/$slug", slug: "faculdades" },
  },
  {
    id: "ecommerce-conversao",
    filters: ["ecommerce"],
    segment: "E-commerce e Varejo",
    highlight: "+58%",
    highlightLabel: "conversão pelo WhatsApp",
    challenge: "Carrinhos abandonados e dúvidas de frete sem resposta rápida.",
    what: "Atendimento omnichannel + IA recuperando contato e fechando venda.",
    result: "Mais pedidos no mesmo tráfego — sem aumentar o time.",
  },
  {
    id: "concessionarias-leads",
    filters: ["concessionarias"],
    segment: "Concessionárias",
    highlight: "+58%",
    highlightLabel: "visitas e test drives marcados",
    challenge: "Leads de alto valor esfriando por demora no follow-up.",
    what: "Distribuição automática, qualificação por IA e CRM por vendedor.",
    result: "Nenhum lead caro fica sem resposta no primeiro minuto.",
    link: { to: "/segmentos/$slug", slug: "concessionarias" },
  },
  {
    id: "condominios-ocorrencias",
    filters: ["condominios"],
    segment: "Condomínios",
    highlight: "-60%",
    highlightLabel: "ligações repetitivas na portaria",
    challenge: "Boletos, ocorrências e reservas chegando por todo lado.",
    what: "Central única no WhatsApp com IA respondendo dúvidas frequentes.",
    result: "Síndico e portaria com o tempo de volta para o que importa.",
    link: { to: "/segmentos/$slug", slug: "condominios" },
  },
];

// ---------- Contador animado ----------
function useCountUp(target: number, durationMs = 1400) {
  // Inicia com o valor final para que crawlers/IA leiam o número real no HTML SSR.
  const [value, setValue] = useState(target);
  const ref = useRef<HTMLDivElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            setValue(0);
            const start = performance.now();
            const tick = (now: number) => {
              const p = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, durationMs]);

  return { value, ref };
}

function StatCounter({
  value,
  suffix,
  prefix,
  label,
}: {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
}) {
  const { value: v, ref } = useCountUp(value);
  return (
    <div ref={ref} className="rounded-2xl border border-border bg-card/40 p-5 text-center">
      <div className="gradient-text text-3xl font-bold tracking-tight md:text-4xl">
        {prefix}
        {v.toLocaleString("pt-BR")}
        {suffix}
      </div>
      <div className="mt-1 text-xs text-muted-foreground md:text-sm">{label}</div>
    </div>
  );
}

function CasesPage() {
  const [filter, setFilter] = useState<Filter>("todos");
  const filtered = useMemo(
    () => (filter === "todos" ? caseCards : caseCards.filter((c) => c.filters.includes(filter))),
    [filter],
  );

  return (
    <>
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="mx-auto max-w-7xl px-4 pt-24 text-xs text-muted-foreground sm:px-6 lg:px-8"
      >
        <ol className="flex flex-wrap items-center gap-1">
          <li>
            <Link to="/" className="hover:text-foreground">Início</Link>
          </li>
          <ChevronRight className="size-3" />
          <li className="text-foreground/80">Cases</li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="gradient-hero relative overflow-hidden pt-8 pb-16">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl"
          >
            Resultados que <span className="gradient-text">se medem</span>
          </motion.h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
            A Kentro não promete — comprova. Veja os números reais de quem organizou atendimento,
            vendas e marketing em uma só plataforma com IA.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="gradient-primary text-white shadow-glow hover:opacity-95">
              <Link to="/contato">Agendar demonstração</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/15 bg-white/5">
              <a
                href={KENTRO.whatsappComercialUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="size-4" /> Falar no WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* NÚMEROS AGREGADOS */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
              O impacto da Kentro em <span className="gradient-text">números</span>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Números agregados da base Kentro — atualizados continuamente.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {aggregateStats.map((s) => (
              <StatCounter key={s.label} {...s} />
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="size-4 text-primary-glow" />
            Startup Destaque — InovAtiva Brasil 2023.2
          </div>
        </div>
      </section>

      {/* CASE EM DESTAQUE — ISP */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center gap-2 text-xs uppercase tracking-wider text-primary-glow">
            <Sparkles className="size-3.5" />
            Case em destaque
          </div>
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
            Provedores de Internet com <span className="gradient-text">Kentro ISP</span>
          </h2>

          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            <article className="glass-card rounded-2xl p-6">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Desafio
              </div>
              <p className="mt-3 text-sm text-foreground/90">
                Atendimento e cobrança fragmentados, alto custo operacional e inadimplência fora de
                controle.
              </p>
            </article>
            <article className="glass-card rounded-2xl p-6">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Solução
              </div>
              <p className="mt-3 text-sm text-foreground/90">
                Kentro ISP — omnichannel + IA + automação de cobrança (boleto e 2ª via no WhatsApp)
                e gestão de chamados com SLA.
              </p>
            </article>
            <article className="glass-card rounded-2xl p-6">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Resultado
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/90">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />
                  -52% de custo com atendimento
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />
                  -28% de inadimplência com cobrança automática
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />
                  Melhora consistente de NPS
                </li>
              </ul>
            </article>
          </div>

          {/* Slot editável para case nominado */}
          <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/30 p-6 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-primary-glow">
                Slot editável
              </span>
              <span>
                Espaço reservado para nome do provedor, logo e depoimento — preencher apenas com
                case real autorizado.
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Button asChild variant="outline" className="border-white/15 bg-white/5">
              <Link to="/segmentos/$slug" params={{ slug: "provedores-de-internet" }}>
                Ver a página de Provedores
                <ChevronRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FILTRO POR SEGMENTO + GRADE DE CASES */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
            Resultados por <span className="gradient-text">segmento</span>
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Filtre e veja como a Kentro entrega resultado no seu contexto.
          </p>

          {/* Chips */}
          <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filtrar por segmento">
            {segmentFilters.map((s) => {
              const active = filter === s.id;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(s.id)}
                  className={
                    "rounded-full border px-3.5 py-1.5 text-sm font-medium transition " +
                    (active
                      ? "border-primary-glow/50 bg-primary/15 text-foreground shadow-glow"
                      : "border-border bg-card/30 text-muted-foreground hover:text-foreground")
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Grade */}
          <h3 className="sr-only">Histórias de quem parou de perder cliente</h3>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c, i) => (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="glass-card flex flex-col rounded-2xl p-6 transition hover:-translate-y-1 hover:shadow-glow"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-primary-glow">
                    {c.segment}
                  </span>
                  {c.verified ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-primary-glow/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary-glow"
                      title="Case verificado"
                    >
                      <ShieldCheck className="size-3" /> verificado
                    </span>
                  ) : (
                    <TrendingUp className="size-4 text-primary-glow" />
                  )}
                </div>

                <div className="mt-5">
                  <div className="gradient-text text-3xl font-bold md:text-4xl">{c.highlight}</div>
                  <div className="text-sm text-muted-foreground">{c.highlightLabel}</div>
                </div>

                <dl className="mt-5 space-y-2.5 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Desafio</dt>
                    <dd className="text-foreground/90">{c.challenge}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">O que a Kentro fez</dt>
                    <dd className="text-foreground/90">{c.what}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Resultado</dt>
                    <dd className="text-foreground/90">{c.result}</dd>
                  </div>
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/5 pt-4">
                  <Button asChild size="sm" className="gradient-primary text-white">
                    <Link to="/contato">Quero resultado parecido</Link>
                  </Button>
                  {c.link ? (
                    <Link
                      to={c.link.to}
                      params={{ slug: c.link.slug }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Ver segmento <ChevronRight className="size-3" />
                    </Link>
                  ) : null}
                </div>
              </motion.article>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="mt-10 text-center text-sm text-muted-foreground">
              Em breve, mais cases nesse segmento. Quer ser o próximo?{" "}
              <Link to="/contato" className="text-primary-glow hover:underline">
                Fale com a gente
              </Link>
              .
            </p>
          ) : null}
        </div>
      </section>

      {/* METODOLOGIA */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
            Como a Kentro mede resultado <span className="gradient-text">de verdade</span>
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Medimos o que importa — não só "tempo de resposta", mas resolução, retomada, reabertura
            e satisfação por etapa. Você enxerga o problema antes do cliente reclamar — e prova o
            ROI da operação.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              {
                icon: Gauge,
                title: "Tempo de resolução",
                desc: "Não só de resposta — o tempo até o cliente sair do problema.",
              },
              {
                icon: RefreshCcw,
                title: "Retomada e reabertura",
                desc: "Quantos atendimentos voltam — e quanto antes você intervém.",
              },
              {
                icon: Wallet,
                title: "Custo por atendimento e lead",
                desc: "Quanto cada conversa custa e o ROI por canal.",
              },
              {
                icon: Target,
                title: "Conversão e inadimplência",
                desc: "Conversão por canal e recuperação financeira em tempo real.",
              },
            ].map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="glass-card rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary/15 text-primary-glow">
                      <Icon className="size-4" />
                    </div>
                    <div className="font-semibold">{m.title}</div>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline" className="border-white/15 bg-white/5">
              <Link to="/gestao-de-atendimento">Conhecer a gestão de atendimento</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/15 bg-white/5">
              <Link to="/ia">Ver a IA da Kentro</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Perguntas frequentes</h2>
          <div className="mt-6 space-y-3">
            {[
              {
                q: "Esses resultados são garantidos?",
                a: "Os números refletem resultados reais de clientes da Kentro. Cada operação varia conforme contexto, processos e maturidade da implementação.",
              },
              {
                q: "Em quanto tempo vejo resultado?",
                a: "Muitos ganhos — organização da fila, redução de retrabalho e SLA — aparecem nas primeiras semanas. Indicadores de receita e inadimplência costumam consolidar entre 60 e 90 dias.",
              },
              {
                q: "Serve para o meu segmento?",
                a: "A Kentro atende farmácias, clínicas, ISPs, educação, e-commerce, concessionárias, condomínios e mais — com playbooks e IA por segmento.",
              },
              {
                q: "Como vocês comprovam os números?",
                a: "Através dos dashboards e KPIs da própria operação do cliente — tempo de resolução, retomada, reabertura, conversão por canal e inadimplência.",
              },
            ].map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60"
              >
                <summary className="cursor-pointer list-none font-semibold">
                  {f.q}
                  <span className="float-right text-muted-foreground group-open:hidden">+</span>
                  <span className="float-right hidden text-muted-foreground group-open:inline">−</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
      <ContactBlock source="cases" eyebrow="Seu case pode ser o próximo" />
    </>
  );
}
